import { Injectable, TemplateRef } from '@angular/core';
import { DatatableFilter } from './datatable-filter';
import { DatatableResponse } from './datatable-response';
import { Observable } from 'rxjs/';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { DatatableColumn } from './datatable-column';
import { WillHandleErrorMessagingOption } from '../utility/http-options';

@Injectable({
  providedIn: 'root',
})
export class DatatableService {
  readonly ACTION_COL_NAME = 'actionsColumn';

  storedFilterLastUrl: string;
  storedFilters: object[];

  constructor(private http: HttpClient) {}

  addFilter(
    filters: DatatableFilter[],
    filter: DatatableFilter,
    filteredColumns: boolean[],
  ): boolean {
    filters.push(filter);
    filteredColumns[filter.property] = true;
    return true;
  }

  removeFilter(
    filters: DatatableFilter[],
    filter: DatatableFilter,
    filteredColumns: boolean[],
  ): any {
    let indexToRemove;
    do {
      indexToRemove = filters.findIndex(f => f.property === filter.property);

      if (indexToRemove !== -1) {
        filters.splice(indexToRemove, 1);

        filteredColumns[filter.property] = false;
      }
    } while (indexToRemove !== -1);
  }

  removeFilterByGroup(
    filters: DatatableFilter[],
    filter: DatatableFilter,
    filteredColumns: boolean[],
  ): any {
    let indexToRemove;
    do {
      indexToRemove = filters.findIndex(f => f.filterGroup && f.filterGroup === filter.filterGroup);

      if (indexToRemove !== -1) {
        filters.splice(indexToRemove, 1);

        filteredColumns[filter.property] = false;
      }
    } while (indexToRemove !== -1);
  }

  getDatatable(url: string, dtRequestPayload: object): Observable<DatatableResponse> {
    return this.http
      .post(url, dtRequestPayload, WillHandleErrorMessagingOption)
      .pipe(map(response => response as DatatableResponse));
  }

  getDatatableAsCsv(url: string, dtRequestPayload: object): Observable<any> {
    const header = new HttpHeaders({ Accept: 'text/csv' });
    return this.http.post(url, dtRequestPayload, { headers: header, responseType: 'text' });
  }

  getColumnInfo(cols: DatatableColumn[], property: string) {
    const col = cols.find(c => c.property === property);

    if (col == null) {
      throw new Error('No column was found with property name of ' + property);
    }

    return col;
  }

  convertInputColumnsToDtColumns(cols: DatatableColumn[]): any[] {
    const result = [];

    for (const col of cols) {
      result.push({
        name: col.title,
        prop: col.property,
        width: col.width,
        resizeable: col.resizable ? col.resizable : true,
        canAutoResize: !col.width,
        cellTemplate: col.template,
        sortable: col.disableSorting !== true,
        flexGrow: col.flexGrow,
      });
    }

    return result;
  }

  addGenericHeaders(dtColumns: any[], genericHeaderTmpl: TemplateRef<any>): any {
    for (let i = 0; i < dtColumns.length; i++) {
      dtColumns[i].headerTemplate = genericHeaderTmpl;
    }
  }

  addActionColumn(
    cols: any[],
    headerTmpl: TemplateRef<any>,
    bodyTmpl: TemplateRef<any>,
    width: number,
  ): any[] {
    cols.push({
      name: this.ACTION_COL_NAME,
      width: width,
      headerTemplate: headerTmpl,
      cellTemplate: bodyTmpl,
      draggable: false,
      resizeable: false,
      sortable: false,
      canAutoResize: false,
    });

    return cols;
  }

  removeActionColumn(cols: any[]): any[] {
    return cols.filter(c => c.name !== this.ACTION_COL_NAME);
  }

  addDetailColumn(
    cols: any[],
    detailToggleTmpl: TemplateRef<any>,
    rowDetailTmpl: TemplateRef<any>,
  ): any {
    if (rowDetailTmpl) {
      cols.unshift({
        width: 50,
        cellTemplate: detailToggleTmpl,
        draggable: false,
        resizeable: false,
        sortable: false,
        canAutoResize: false,
      });
    }

    return cols;
  }

  getCounts(totalRows: number, currentPage: number, pageSize: number): any {
    const result: any = {};

    result.totalCount = totalRows;
    result.filteredCount = totalRows;
    result.beginningCount = Math.min(result.filteredCount, (currentPage - 1) * pageSize + 1);
    result.endCount = Math.min(currentPage * pageSize, result.filteredCount);

    return result;
  }

  getPages(filteredCount: number, currentPage: number, pageSize: number): any {
    const result: any = {};

    result.totalPages = Math.ceil(filteredCount / pageSize);
    if (currentPage <= 3) {
      result.minPage = 1;
      result.maxPage = Math.min(result.totalPages, 5);
    } else if (currentPage >= result.totalPages - 3) {
      result.minPage = Math.max(result.totalPages - 5, 1);
      result.maxPage = result.totalPages;
    } else {
      result.minPage = Math.max(currentPage - 2, 1);
      result.maxPage = Math.min(currentPage + 2, result.totalPages);
    }
    result.pagesArray = Array(result.maxPage - result.minPage + 1)
      .fill(0)
      .map((x, i) => i + result.minPage); // eslint-disable-line @typescript-eslint/no-unused-vars

    return result;
  }
}
